// ── TicketForm.js ────────────────────────────────────────────────────
// Player-facing bug report / support ticket submission modal.
// Opened from TopBar (available in-game and out-of-game).

import React, { useState, useEffect, useRef } from 'react';
import { collection, addDoc, doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { TICKET_CATEGORIES, detectBrowserDevice } from './ticketsDatabase';

const S = {
  anton: { fontFamily: "'Anton', sans-serif" },
  mono: { fontFamily: "'Monda', sans-serif" },
};

const red = '#e01010';
const dim = 'rgba(255,255,255,0.12)';
const faint = 'rgba(255,255,255,0.04)';
const logoMain = process.env.PUBLIC_URL + '/logo-main.png';

// ── Custom category dropdown (not a native <select> — full color control) ──
function CategoryDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  let selectedLabel = 'Select an issue type...';
  for (const g of TICKET_CATEGORIES) {
    const found = g.options.find(o => o.value === value);
    if (found) { selectedLabel = found.label; break; }
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen(p => !p)}
        style={{
          width: '100%', textAlign: 'left', background: faint, border: '1px solid ' + (open ? 'rgba(224,16,16,0.5)' : dim),
          borderRadius: '6px', color: value ? '#fff' : 'rgba(255,255,255,0.3)', fontFamily: "'Monda',sans-serif", fontSize: '0.6rem',
          padding: '11px 14px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
        <span>{selectedLabel}</span>
        <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.5rem', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>▼</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 10,
          background: '#0e0e0e', border: '1px solid rgba(224,16,16,0.3)', borderRadius: '8px',
          maxHeight: '260px', overflowY: 'auto', boxShadow: '0 12px 32px rgba(0,0,0,0.6)',
        }}>
          {TICKET_CATEGORIES.map(g => (
            <div key={g.group}>
              <div style={{ ...S.mono, padding: '8px 14px 4px', fontSize: '0.38rem', letterSpacing: '1.5px', color: red, textTransform: 'uppercase', opacity: 0.85 }}>
                {g.group}
              </div>
              {g.options.map(o => (
                <div key={o.value} onClick={() => { onChange(o.value); setOpen(false); }}
                  style={{ ...S.mono, padding: '9px 16px', fontSize: '0.58rem', color: value === o.value ? '#fff' : 'rgba(255,255,255,0.65)', cursor: 'pointer', background: value === o.value ? 'rgba(224,16,16,0.12)' : 'transparent', transition: 'background 0.12s' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(224,16,16,0.12)'}
                  onMouseLeave={e => e.currentTarget.style.background = value === o.value ? 'rgba(224,16,16,0.12)' : 'transparent'}>
                  {o.label}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function TicketForm({ onClose }) {
  const [user] = useAuthState(auth);
  const [displayName, setDisplayName] = useState('');
  const [category, setCategory] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const browserDevice = detectBrowserDevice();

  useEffect(() => {
    if (!user) return;
    getDoc(doc(db, 'users', user.uid)).then(snap => {
      if (snap.exists() && snap.data().displayName) setDisplayName(snap.data().displayName);
      else setDisplayName(user.displayName || user.email?.split('@')[0] || '');
    }).catch(() => setDisplayName(user.displayName || user.email?.split('@')[0] || ''));
  }, [user]);

  const canSubmit = category && title.trim().length >= 4 && description.trim().length >= 10 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit || !user) return;
    setSubmitting(true);
    setError('');
    try {
      await addDoc(collection(db, 'tickets'), {
        userId: user.uid,
        displayName: displayName || user.email || 'Unknown',
        category,
        title: title.trim(),
        description: description.trim(),
        stepsToReproduce: steps.trim() || null,
        browserDevice,
        status: 'open',
        createdAt: serverTimestamp(),
      });
      setSubmitted(true);
      setTimeout(() => onClose && onClose(), 1800);
    } catch (e) {
      setError('Could not submit your ticket. Please try again.');
      console.error('Ticket submit error:', e);
    }
    setSubmitting(false);
  };

  const fieldLabel = (text) => (
    <div style={{ ...S.mono, fontSize: '0.4rem', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', marginBottom: '6px' }}>{text}</div>
  );

  const inputStyle = {
    width: '100%', background: faint, border: '1px solid ' + dim, borderRadius: '6px',
    color: '#fff', fontFamily: "'Monda',sans-serif", fontSize: '0.6rem', padding: '9px 12px',
    outline: 'none', resize: 'vertical',
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(12px)' }}
      onClick={onClose}>
      <div style={{ background: 'rgba(6,6,6,0.98)', border: '1px solid rgba(224,16,16,0.3)', borderRadius: '16px', width: '100%', maxWidth: '540px', maxHeight: '90vh', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid rgba(224,16,16,0.15)', display: 'flex', alignItems: 'center', gap: '14px', position: 'sticky', top: 0, background: 'rgba(6,6,6,0.98)', zIndex: 1 }}>
          <img src={logoMain} alt="" style={{ width: '34px', height: '34px', objectFit: 'contain', flexShrink: 0 }} onError={e => { e.target.style.display = 'none'; }} />
          <div>
            <div style={{ ...S.anton, fontSize: '0.9rem', letterSpacing: '3px', color: red }}>REPORT AN ISSUE</div>
            <div style={{ ...S.mono, fontSize: '0.4rem', color: 'rgba(255,255,255,0.3)', marginTop: '2px', letterSpacing: '1px' }}>Help us track down what went wrong</div>
          </div>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '4px', color: 'rgba(255,255,255,0.3)', cursor: 'pointer', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem' }}>✕</button>
        </div>

        {submitted ? (
          <div style={{ padding: '48px 24px', textAlign: 'center' }}>
            <div style={{ ...S.anton, fontSize: '0.9rem', letterSpacing: '2px', color: '#2ecc71', marginBottom: '8px' }}>✓ TICKET SUBMITTED</div>
            <div style={{ ...S.mono, fontSize: '0.5rem', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.5px' }}>Thanks — track it anytime from Settings → My Tickets.</div>
          </div>
        ) : (
          <>
            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '18px' }}>

              <div>
                {fieldLabel('Category')}
                <CategoryDropdown value={category} onChange={setCategory} />
              </div>

              <div>
                {fieldLabel('Title')}
                <input type="text" value={title} onChange={e => setTitle(e.target.value)}
                  placeholder='e.g. "My save data disappeared after logging in"'
                  maxLength={120}
                  style={inputStyle} />
              </div>

              <div>
                {fieldLabel('Description')}
                <textarea value={description} onChange={e => setDescription(e.target.value)}
                  placeholder="What were you doing when it happened? What did you expect, and what actually happened?"
                  style={{ ...inputStyle, minHeight: '100px', lineHeight: 1.6 }} />
              </div>

              <div>
                {fieldLabel('Steps to reproduce (optional)')}
                <textarea value={steps} onChange={e => setSteps(e.target.value)}
                  placeholder={'1. ...\n2. ...\n3. ...'}
                  style={{ ...inputStyle, minHeight: '70px', lineHeight: 1.6 }} />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', ...S.mono, fontSize: '0.42rem', color: 'rgba(255,255,255,0.3)', letterSpacing: '0.5px' }}>
                <span style={{ color: 'rgba(255,255,255,0.2)' }}>Browser & device:</span> {browserDevice}
              </div>

              {error && (
                <div style={{ ...S.mono, fontSize: '0.46rem', color: '#e74c3c', letterSpacing: '0.5px' }}>{error}</div>
              )}
            </div>

            {/* Footer */}
            <div style={{ padding: '16px 24px', borderTop: '1px solid rgba(224,16,16,0.1)', display: 'flex', justifyContent: 'flex-end', gap: '8px', position: 'sticky', bottom: 0, background: 'rgba(6,6,6,0.98)' }}>
              <button onClick={onClose}
                style={{ ...S.mono, fontSize: '0.46rem', letterSpacing: '1px', padding: '8px 18px', borderRadius: '5px', cursor: 'pointer', background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.35)' }}>
                CANCEL
              </button>
              <button onClick={handleSubmit} disabled={!canSubmit}
                style={{ ...S.anton, fontSize: '0.56rem', letterSpacing: '2px', padding: '8px 24px', borderRadius: '5px', cursor: canSubmit ? 'pointer' : 'not-allowed',
                  background: canSubmit ? 'rgba(224,16,16,0.15)' : 'transparent',
                  border: '1px solid ' + (canSubmit ? 'rgba(224,16,16,0.6)' : dim),
                  color: canSubmit ? red : 'rgba(255,255,255,0.2)' }}>
                {submitting ? 'SUBMITTING...' : 'SUBMIT TICKET'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
